import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  Gauge, Users, Factory, Car, Truck, Package, UserGear, SignOut,
  List, CaretDown, Globe, Bell, CarProfile, TelegramLogo, PlugsConnected, ChartBar, Warehouse, X, Anchor, Stack, Receipt, Question, Lightning, ChatsTeardrop, ClipboardText, Buildings
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import ChatWidget from '../components/ChatWidget';
import DemoBanner from '../components/DemoBanner';
import BrandLogo from '../components/BrandLogo';
import { useSystemInfo } from '../hooks/useSystemInfo';
import { getChatUnreadCount, getOrderDraftCount } from '../services/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';

const AdminLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { brand_name } = useSystemInfo();

  // Reflect brand in the browser tab title (Loom recordings see this too).
  useEffect(() => {
    document.title = `${brand_name} — Order Manager`;
  }, [brand_name]);

  const restricted = !['admin', 'master_admin'].includes(user?.role) && user?.permissions?.see_own_orders_only;
  const isMaster = user?.role === 'master_admin';
  // Privileged roles: behave like admin throughout the sidebar / nav. master_admin
  // is the developer / system owner — has every admin capability plus dealer mgmt.
  const isAdminLike = ['admin', 'master_admin'].includes(user?.role);

  // Live counters (chat unread + pending drafts) for sidebar badges
  const [unreadChat, setUnreadChat] = useState(0);
  const [pendingDrafts, setPendingDrafts] = useState(0);
  useEffect(() => {
    if (!user || user.role === 'client') return;
    let mounted = true;
    const tick = async () => {
      try {
        const r = await getChatUnreadCount();
        if (mounted) setUnreadChat(r.data?.unread || 0);
      } catch { /* ignore */ }
      if (['admin', 'master_admin', 'logistics_manager'].includes(user.role)) {
        try {
          const r = await getOrderDraftCount();
          if (mounted) setPendingDrafts(r.data?.pending || 0);
        } catch { /* ignore */ }
      }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { mounted = false; clearInterval(id); };
  }, [user]);

  const navItems = [
    // ===== CORE WORKFLOW =====
    ...(!restricted ? [{ path: '/dashboard', icon: Gauge, label: t('nav.dashboard') }] : []),
    { path: '/clients', icon: Users, label: t('nav.clients') },
    // Dealer management — master_admin only (developer/system owner).
    ...(isMaster ? [{ path: '/dealers', icon: Buildings, label: 'Dealers' }] : []),
    ...(isMaster ? [{ path: '/companies', icon: Buildings, label: 'Companies' }] : []),
    { path: '/orders', icon: Package, label: t('nav.orders') },
    ...(['admin', 'master_admin', 'logistics_manager'].includes(user?.role) ? [{ path: '/order-drafts', icon: ClipboardText, label: 'Order Drafts', badge: pendingDrafts }] : []),
    { path: '/containers', icon: Truck, label: 'Containers' },
    {
      label: t('nav.catalog'),
      icon: Car,
      children: [
        { path: '/brands', label: t('nav.brands') },
        { path: '/models', label: t('nav.models') },
      ]
    },
    { path: '/vehicles', icon: Warehouse, label: 'Stock' },

    // ===== LOGISTICS & CUSTOMS =====
    ...(!restricted ? [{ section: 'logistics', label: 'Logistics & Customs' }] : []),
    ...(!restricted ? [{ path: '/loading-groups', icon: Stack, label: 'Loading Groups' }] : []),
    ...(['admin', 'master_admin', 'logistics_manager', 'operations'].includes(user?.role) ? [{ path: '/ports', icon: Anchor, label: 'Dédouanement' }] : []),
    ...(['admin', 'master_admin', 'logistics_manager', 'operations'].includes(user?.role) ? [{ path: '/douane-pos', icon: Receipt, label: 'Douane POs' }] : []),
    ...(!restricted ? [{ path: '/shipping', icon: Truck, label: t('nav.shipping') }] : []),
    ...(!restricted ? [{ path: '/suppliers', icon: Factory, label: t('nav.suppliers') }] : []),

    // ===== INSIGHTS =====
    ...(['admin', 'master_admin', 'logistics_manager'].includes(user?.role) ? [
      { section: 'insights', label: 'Insights' },
      { path: '/reports', icon: ChartBar, label: 'Reports' },
    ] : []),

    // ===== COMMUNICATION =====
    ...(!restricted && user?.role !== 'client' ? [
      { section: 'communication', label: 'Communication' },
      { path: '/messages', icon: ChatsTeardrop, label: 'Messages', badge: unreadChat },
    ] : []),

    // ===== SETTINGS =====
    ...(['admin', 'master_admin', 'logistics_manager'].includes(user?.role) ? [
      { section: 'settings', label: 'Settings' },
      { path: '/automations', icon: Lightning, label: 'Automations' },
    ] : []),
    ...(isAdminLike ? [
      { path: '/employees', icon: UserGear, label: t('nav.employees') },
    ] : []),
    ...(['admin', 'master_admin', 'logistics_manager'].includes(user?.role) ? [
      { path: '/telegram', icon: TelegramLogo, label: 'Telegram' },
      { path: '/integrations', icon: PlugsConnected, label: 'Data Export' },
    ] : []),
    { path: '/help', icon: Question, label: 'Help' },
  ];

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'ar', label: 'العربية' },
    { code: 'zh', label: '中文' }
  ];

  const isActive = (path) => location.pathname === path;
  const isParentActive = (children) => children?.some(c => location.pathname === c.path);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-background flex flex-col" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      <DemoBanner />
      <div className="flex flex-1 min-h-0">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] lg:hidden" onClick={closeSidebar} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-[70]
        w-64 bg-white border-r border-border flex flex-col
        transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-border">
          <div className="flex items-center">
            <BrandLogo size={32} alt={brand_name} />
            <span className="ml-2 font-bold text-sm tracking-tight leading-tight">{brand_name}<br/><span className="text-[10px] font-medium text-muted-foreground">Order Manager</span></span>
          </div>
          <button onClick={closeSidebar} className="lg:hidden p-1.5 hover:bg-secondary rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {navItems.map((item) => (
            item.section ? (
              <div key={`section-${item.section}`} className="mt-4 mb-1 px-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{item.label}</p>
              </div>
            ) : item.children ? (
              <div key={item.label} className="mb-1">
                <div className={`nav-item ${isParentActive(item.children) ? 'text-foreground' : ''}`}>
                  <item.icon weight="duotone" className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  <CaretDown className="w-4 h-4" />
                </div>
                <div className="ml-8 space-y-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={closeSidebar}
                      className={`block px-4 py-2 text-sm ${isActive(child.path) ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`nav-item ${isActive(item.path) ? 'nav-item-active' : ''}`}
              >
                <item.icon weight="duotone" className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="text-[10px] bg-red-600 text-white rounded-full px-1.5 py-0.5 ml-2">{item.badge}</span>
                )}
              </Link>
            )
          ))}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-medium text-sm">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Header */}
        <header className="h-14 bg-white border-b border-border flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-secondary rounded"
            data-testid="sidebar-toggle"
          >
            <List className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 px-2" data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs">{languages.find(l => l.code === i18n.language)?.label || 'EN'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? 'bg-secondary' : ''}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 px-2" data-testid="user-menu">
                  <div className="w-7 h-7 rounded-sm bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-xs">{user?.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                  <SignOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-3 sm:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
      <ChatWidget user={user} />
      </div>
    </div>
  );
};

export default AdminLayout;
